import {
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges, OnDestroy,
} from "@angular/core";
import { ChartDataService } from "../../../helpers/chart-data.service";
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import { ReviewsService } from "../reviews.service";


interface ProcessedDataItem {
  xValue: string; 
  yValue: number;
  yLabel: string;
}

@Component({
  selector: "app-reviews-percentile-grouped-by-store",
  template: `<div id="lineDiv" style="width: 100%; height: 300px;"></div>`,
  styleUrl: "./reviews-percentile-grouped-by-store.component.scss",
})
export class ReviewsPercentileGroupedByStoreComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input() startDate!: Date;
  @Input() endDate!: Date;
  @Input() categoryIds!: number[];
  @Input() storeIds!: number[];
  @Input() companyId!: number;

  colorMapping: Record<string, string> = {
    "IKEA meubelmontage": "#5AAF5C",
    "IKEA badkamer installatie": "#457DA0",
    "IKEA apparatuur installatie": "#FFCB4A",
    "IKEA B2B Meubelmontage": "#DA70D6",
    "IKEA B2B Keukeninstallatie": "#FFAA39",
    "Leenbakker meubelmontage": "#5AAF5C"
  };

  private root!: am5.Root;

  ngOnChanges(changes: SimpleChanges): void {
    
    if (
      changes["categoryIds"] ||
      changes["companyId"] ||
      changes["startDate"]?.currentValue ||
      changes["endDate"]?.currentValue ||
      changes["storeIds"]
    ) {
      this.loadChartData();
    }
  }

  constructor(
    private reviewService: ReviewsService,
    private chartDataService: ChartDataService
  ) {}

  ngOnInit(): void {
    const companyIdval = localStorage.getItem("companyId");
    this.companyId = companyIdval ? Number.parseInt(companyIdval, 10) : 0;
  }

  
  loadChartData() {
    const params = {
      categoryIds: this.categoryIds,
      StoreIds: this.storeIds,
      startDate: this.startDate,
      endDate: this.endDate,
      companyId: this.companyId,
    };
    if (
      !params.startDate ||
      !params.endDate ||
      !params.categoryIds ||
      !params.StoreIds ||
      !params.companyId
    ) {
      return;
    }
    

    this.reviewService.getPercentileOfReviewByGrouped(params).subscribe(
      (data: any[]) => {
        
        if (this.root) {
          this.root.dispose();
        }

        
        const processedData =
          this.chartDataService.convertXValueFromYearMonthToMonthName(data);

        
        if (this.companyId === 20) {
          // For Leen Bakker: only one yLabel, treat as single series
          this.createSingleSeriesChart(processedData);
        } else if (this.companyId != 4) {
          this.createChart(processedData);
        } else {
          this.createSingleChart(processedData);
        }
      },
      (error) => {
        console.error("Error fetching data", error);
      }
    );
    
  }

  createChart(processedData: any) {
    // Create root element
    this.root = am5.Root.new("lineDiv");

    // Apply themes
    this.root.setThemes([am5themes_Animated.new(this.root)]);
    this.root._logo?.dispose(); // Remove the logo to avoid clutter
    
    // Create chart
    const chart = this.root.container.children.push(
      am5xy.XYChart.new(this.root, {
        panX: false,
        panY: false,
        wheelX: "none",
        wheelY: "none",
        pinchZoomX: true,
      })
    );

    // Create axes
    const xAxis = chart.xAxes.push(
      am5xy.CategoryAxis.new(this.root, {
        categoryField: "xValue",
        renderer: am5xy.AxisRendererX.new(this.root, {
          minGridDistance: 30, 
          inside: false, 
        }),
        tooltip: am5.Tooltip.new(this.root, {}),
      })
    );

    const yMaxVal = Math.max(...processedData.map((item: any) => item.yValue), 100) + 50;
    const yAxis = chart.yAxes.push(
      am5xy.ValueAxis.new(this.root, {
        renderer: am5xy.AxisRendererY.new(this.root, {
          minGridDistance: 40, // Optional: Control gridline distance
        }),
        min: 0, // Set minimum value to 0
        max: yMaxVal,
      })
    );
    // Add data
    const seriesData = this.prepareData(processedData);

    xAxis.data.setAll(seriesData.categories);

    // Create series for each yLabel with color mapping
    seriesData.series.forEach((seriesItem) => {
      const color =
        this.colorMapping[seriesItem.yLabel] || this.colorMapping["Default"];

      // Create a legend
      const legend = am5.Legend.new(this.root, {
        position: "relative", // Or "top", "left", "right"
      });

      
      this.root.container.children.push(legend);

      const series = chart.series.push(
        am5xy.LineSeries.new(this.root, {
          name: seriesItem.yLabel, // Set series name from yLabel
          xAxis: xAxis,
          yAxis: yAxis,
          valueYField: "yValue",
          categoryXField: "xValue",

          
          tooltip: am5.Tooltip.new(this.root, {
            labelText: `{name}[/]: {valueY}[/]`, // Format with color
            getFillFromSprite: true, // Prevent tooltip color inheritance
            getStrokeFromSprite: false,
            pointerOrientation: "horizontal", // Horizontal tooltip orientation
          }),

          // Series appearance
          stroke: am5.color(color),
          fill: am5.color(
            `rgba(${am5.color(color).r}, ${am5.color(color).g}, ${
              am5.color(color).b
            }, 0.3)`
          ), // Set transparent fill
        })
      );

      // Configure stroke for the series via the strokes template
      series.strokes.template.setAll({
        strokeWidth: 2,
        stroke: am5.color(color),
      });

      // Filter out data points where yValue is zero
      const filteredData = seriesItem.data.filter((item) => item.yValue !== 0);
      series.data.setAll(filteredData);

      // If no data points left after filtering, set stroke to undefined
      if (filteredData.length === 0) {
        series.strokes.template.setAll({
          strokeWidth: 0, // Hide the line if no data left (all values were zero)
        });
      } else {
        series.appear();
      }

      // Add tooltips on hover with custom content
      series.bullets.push((root, series, dataItem) => {
        const index = series.dataItems.indexOf(dataItem);

        const bullet = am5.Bullet.new(root, {
          sprite: am5.Circle.new(root, {
            radius: 5,
            fill: am5.color(color), // Ensure bullet matches line color
          }),
        });
        return bullet;
      });
    });
    xAxis.get("renderer").grid.template.set("visible", false);
    
    chart.set("cursor", am5xy.XYCursor.new(this.root, {}));

    // Make chart appear
    chart.appear(1000, 100);
  }

  prepareData(processedData: any) {
    // Now you can safely work with processedData assuming it has the correct structure
    const categories: { xValue: string }[] = [];
    const series: {
      yLabel: string;
      data: { xValue: string; yValue: number }[];
    }[] = [];

    const yLabels: any[] = [
      ...new Set(
        processedData
          .map((item: ProcessedDataItem) => item.yLabel)
          .filter(
            (yLabel: any): yLabel is string =>
              yLabel !== undefined && yLabel !== null
          )
      ),
    ];

    const xValues: any[] = [
      ...new Set(processedData.map((item: ProcessedDataItem) => item.xValue)),
    ];

    xValues.forEach((xValue) => {
      categories.push({ xValue });
    });

    yLabels.forEach((yLabel) => {
      const seriesData = processedData
        .filter((item: ProcessedDataItem) => item.yLabel === yLabel)
        .map((item: any) => ({ xValue: item.xValue, yValue: item.yValue }));
      series.push({ yLabel, data: seriesData });
    });

    return { categories, series };
  }

  createSingleChart(processedData: any) {
    // Dispose of the existing chart to avoid multiple root errors and ensure updates
    if (this.root) {
      this.root.dispose();
    }
    this.root = am5.Root.new("lineDiv");
    
    this.root.setThemes([am5themes_Animated.new(this.root)]);
    this.root._logo?.dispose(); // Remove the logo to avoid clutter

    const chart = this.root.container.children.push(
      am5xy.XYChart.new(this.root, {
      panX: false,
      panY: false,
      wheelX: "none",
      wheelY: "none",
      pinchZoomX: false,
      layout: this.root.verticalLayout,
      })
    );

    // X Axis (Category = Month)
    const xAxis = chart.xAxes.push(
      am5xy.CategoryAxis.new(this.root, {
        categoryField: "xValue",
        renderer: am5xy.AxisRendererX.new(this.root, {
          minGridDistance: 30,
        }),
        tooltip: am5.Tooltip.new(this.root, {}),
      })
    );

    // Y Axis (Value)
    const yAxis = chart.yAxes.push(
      am5xy.ValueAxis.new(this.root, {
        renderer: am5xy.AxisRendererY.new(this.root, {
          minGridDistance: 40, // Optional: Control gridline distance
        }),
        min: 0, // Set minimum value to 0
        max: 100,
        tooltip: am5.Tooltip.new(this.root, {}),
      })
    );

    const data = processedData.map((item: any) => ({
      xValue: item.xValue,
      yValue: item.yValue,
      yLabel: item.yLabel,
    }));
    xAxis.data.setAll(data);
    const color =
      this.colorMapping[data[0].yLabel] || this.colorMapping["Default"];

    xAxis.data.setAll(data);

    // Line Series
    const series = chart.series.push(
      am5xy.LineSeries.new(this.root, {
        name: "Praxis laminaat legge",
        xAxis: xAxis,
        yAxis: yAxis,
        valueYField: "yValue",
        categoryXField: "xValue",
        tooltip: am5.Tooltip.new(this.root, {
          labelText: "Praxis laminaat legge {categoryX}: {valueY}%", // Tooltip content
          pointerOrientation: "horizontal",
        }),
      })
    );

    series.strokes.template.setAll({
      strokeWidth: 3,
      stroke: am5.color(0xff5733),
    });

    // Bullets
    series.bullets.push(() => {
      return am5.Bullet.new(this.root, {
        sprite: am5.Circle.new(this.root, {
          radius: 4,
          fill: am5.color(0x3357FF),
          stroke: am5.color(0x3357FF),
          strokeWidth: 2,
        }),
      });
    });

    series.data.setAll(data);

    chart.set(
      "cursor",
      am5xy.XYCursor.new(this.root, {
        behavior: "none",
        xAxis: xAxis,
        yAxis: yAxis,
      })
    );

    series.appear(1000);
    chart.appear(1000, 100);

    xAxis.get("renderer").grid.template.set("visible", false);
    xAxis.get("renderer").labels.template.set("visible", false);
    // Add cursor (for hovering)
    chart.set("cursor", am5xy.XYCursor.new(this.root, {}));
  }

  // For Leen Bakker (companyId == 20): single yLabel
  createSingleSeriesChart(processedData: any) {
    // Dispose of the existing chart to avoid multiple root errors and ensure updates
    if (this.root) {
      this.root.dispose();
    }
    this.root = am5.Root.new("lineDiv");
    
    this.root.setThemes([am5themes_Animated.new(this.root)]);
    this.root._logo?.dispose(); // Remove the logo to avoid clutter

    const chart = this.root.container.children.push(
      am5xy.XYChart.new(this.root, {
        panX: false,
        panY: false,
        wheelX: "none",
        wheelY: "none",
        pinchZoomX: false,
        layout: this.root.verticalLayout,
      })
    );

    // X Axis (Category = Month)
    const xAxis = chart.xAxes.push(
      am5xy.CategoryAxis.new(this.root, {
        categoryField: "xValue",
        renderer: am5xy.AxisRendererX.new(this.root, {
          minGridDistance: 30,
        }),
        tooltip: am5.Tooltip.new(this.root, {}),
      })
    );

    // Y Axis (Value)
    const yMaxVal = Math.max(...processedData.map((item: any) => item.yValue), 100) + 50;
    const yAxis = chart.yAxes.push(
      am5xy.ValueAxis.new(this.root, {
        renderer: am5xy.AxisRendererY.new(this.root, {
          minGridDistance: 40,
        }),
        min: 0,
        max: yMaxVal,
        tooltip: am5.Tooltip.new(this.root, {}),
      })
    );

    // Get the yLabel from the first data item
    const yLabel = processedData.length > 0 ? processedData[0].yLabel : '';
    const color = this.colorMapping[yLabel] || "#5AAF5C";

    const data = processedData.map((item: any) => ({
      xValue: item.xValue,
      yValue: item.yValue,
    }));

    xAxis.data.setAll(data);

    // Line Series
    const series = chart.series.push(
      am5xy.LineSeries.new(this.root, {
        name: yLabel || "Leenbakker",
        xAxis: xAxis,
        yAxis: yAxis,
        valueYField: "yValue",
        categoryXField: "xValue",
        tooltip: am5.Tooltip.new(this.root, {
          labelText: `{name} {categoryX}: {valueY}%`,
          pointerOrientation: "horizontal",
        }),
      })
    );

    series.strokes.template.setAll({
      strokeWidth: 3,
      stroke: am5.color(color),
    });

    // Bullets
    series.bullets.push(() => {
      return am5.Bullet.new(this.root, {
        sprite: am5.Circle.new(this.root, {
          radius: 4,
          fill: am5.color(color),
          stroke: am5.color(color),
          strokeWidth: 2,
        }),
      });
    });

    series.data.setAll(data);

    chart.set(
      "cursor",
      am5xy.XYCursor.new(this.root, {
        behavior: "none",
        xAxis: xAxis,
        yAxis: yAxis,
      })
    );

    series.appear(1000);
    chart.appear(1000, 100);

    xAxis.get("renderer").grid.template.set("visible", false);
    // Add cursor (for hovering)
    chart.set("cursor", am5xy.XYCursor.new(this.root, {}));
  }

  ngOnDestroy(): void {
    if (this.root) {
      this.root.dispose();
    }
  }

  browserOnly(f: () => void) {
    if (typeof window !== "undefined") {
      f();
    }
  }
}
